import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../../api/axios'
import { Alert } from '../../../components/ui/Alert'
import { nomePf } from '../divisoes'
import { baixarPdf, btnSecundario, cartao, erroApi, n2, n4, td, th } from './comum'

// Apuracao oficial IPSC da etapa, separada por divisao

interface PistaAp { idpista: number; numero: number; nome: string; pontos_maximos_usados: number }
interface PistaComp { pontos: number; tempo: number | null; hit_factor: number; pontos_pista: number }
interface Competidor {
  idinscricao: number; atirador: string; clube: string | null; power_factor: string; squad_numero: number | null
  posicao: number | null; dq: boolean; total: number; percentual: number; pistas: Record<string, PistaComp | null>
}
interface Apuracao { pistas: PistaAp[]; divisoes: Array<{ iddivisao: number; divisao: string; competidores: Competidor[] }> }

export function AbaApuracao({ idetapa }: { idetapa: number }) {
  const [alerta, setAlerta] = useState<string | null>(null)
  const { data, isLoading } = useQuery({
    queryKey: ['ipsc-apuracao', idetapa],
    queryFn: () => api.get<{ data: Apuracao }>(`/ipsc/etapas/${idetapa}/apuracao`).then((r) => r.data.data),
  })
  if (isLoading || !data) return <p className="text-compumaq-textoSuave">Carregando...</p>
  return (
    <div className="space-y-4">
      {alerta && <Alert type="error" message={alerta} onClose={() => setAlerta(null)} />}
      <div className="flex justify-between items-center gap-3">
        <p className="text-sm text-compumaq-textoSuave">
          Em cada pista, o maior Hit Factor da divisão leva os pontos máximos da pista e os demais proporcionalmente; total = soma das pistas;
          % = sobre o 1º colocado da divisão. DQ fica sem classificação.
        </p>
        <button className={btnSecundario} onClick={() => baixarPdf(`/ipsc/etapas/${idetapa}/apuracao/pdf`, 'apuracao-ipsc.pdf').catch((e) => setAlerta(erroApi(e, 'Erro ao gerar o PDF')))}>
          Imprimir / PDF
        </button>
      </div>
      {data.divisoes.length === 0 && <p className={`${cartao} text-center py-12 text-compumaq-textoSuave`}>Nenhum resultado lançado nesta etapa.</p>}
      {data.divisoes.map((dv) => (
        <div key={dv.iddivisao} className={`${cartao} overflow-x-auto`} data-divisao={dv.divisao}>
          <h3 className="px-4 pt-3 font-semibold text-compumaq-doradoClaro">Divisão {dv.divisao} <span className="text-xs text-compumaq-textoSuave font-normal">({dv.competidores.length})</span></h3>
          <table className="w-full text-sm mt-2">
            <thead className="bg-compumaq-preto border-y border-compumaq-dourado/25">
              <tr>
                <th className={th}>Pos</th><th className={th}>Atirador</th><th className={th}>PF</th>
                {data.pistas.map((p) => <th key={p.idpista} className={`${th} text-right`} title={`${p.nome} — ${p.pontos_maximos_usados} pts máx.`}>P{p.numero}</th>)}
                <th className={`${th} text-right`}>Total</th><th className={`${th} text-right`}>%</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-compumaq-dourado/25">
              {dv.competidores.map((c) => (
                <tr key={c.idinscricao} className={c.dq ? 'text-red-400' : c.posicao === 1 ? 'font-semibold' : ''}>
                  <td className={td}>{c.dq ? 'DQ' : `${c.posicao}º`}</td>
                  <td className={td}>{c.atirador}<span className="block text-xs text-compumaq-textoSuave">{c.clube ?? ''}{c.squad_numero ? ` · Squad ${c.squad_numero}` : ''}</span></td>
                  <td className={td}>{nomePf(c.power_factor)}</td>
                  {data.pistas.map((p) => {
                    const pp = c.pistas[String(p.idpista)]
                    return (
                      <td key={p.idpista} className={`${td} text-right whitespace-nowrap`}>
                        {c.dq ? '—' : pp ? <>{n4(pp.pontos_pista)}<span className="block text-xs text-compumaq-textoSuave">HF {n4(pp.hit_factor)}</span></> : <span className="text-compumaq-textoSuave">0</span>}
                      </td>
                    )
                  })}
                  <td className={`${td} text-right`}>{c.dq ? 'DQ' : n4(c.total)}</td>
                  <td className={`${td} text-right`}>{c.dq ? '' : `${n2(c.percentual)}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}
